import React from 'react';

import CheckBoxField from 'sharedreact/components/CheckBoxField';
import ParameterSet from 'sharedreact/components/ParameterSet';
import TextField from 'sharedreact/components/TextField';
import hideable from 'sharedreact/components/hideable';
import { translate } from 'sharedreact/utils/LanguageSupport';
import LdapConfig from 'workspaces/launcher/data/LdapConfig';
import Dialog from 'sharedreact/components/Dialog';
import ValidationForm, { ValidationProps, ValidationWrapper } from 'sharedreact/components/ValidationForm';

const PASSWORD_PLACEHOLDER: string = 'dummy123';

type Props = {
  show: boolean,
  language: string,
  ldapConfig: LdapConfig | null,

  onClose: () => void,
  onSave: (ldapConfig: LdapConfig) => void,
  onChangeValue: (name: string, value: any) => void,
} & ValidationProps;

type State = {
  isDirty: boolean
};

class EditLdapConfigDialogImpl extends React.Component<Props, State> {
  private _edited: string[];
  private _required: string[];

  constructor (props: Props) {
    super(props);

    this.state = {
      isDirty: false
    };

    this._edited = [];
    this._required = ['server', 'systemUser', 'password', 'udn'];

    this.handleRequiredChange = this.handleRequiredChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
  }

  override componentDidMount (): void {
    if (this.props.ldapConfig != null) {
      const ldapConfig: LdapConfig = this.props.ldapConfig;
      this._edited = [];

      if (ldapConfig.server !== '') {
        this._edited.push('server');
      }
      if (ldapConfig.systemUser !== '') {
        this._edited.push('systemUser');
      }
      if (!ldapConfig.isPasswordEdited || ldapConfig.password !== '') {
        // password from server is not filled in and a placeholder is displayed instead
        // so if password was not edited, assume it is filled in
        this._edited.push('password');
      }
      if (ldapConfig.udn !== '') {
        this._edited.push('udn');
      }
    }
  }

  clearDirty (): void {
    this.setState({'isDirty': false});
  }

  override componentDidUpdate (prevProps: Readonly<Props>): void {
    if (this.props.ldapConfig != null && prevProps.ldapConfig == null) {
      const ldapConfig: LdapConfig = this.props.ldapConfig;
      this._edited = [];
      this.clearDirty();

      if (ldapConfig.server !== '') {
        this._edited.push('server');
      }
      if (ldapConfig.systemUser !== '') {
        this._edited.push('systemUser');
      }
      if (!ldapConfig.isPasswordEdited || ldapConfig.password !== '') {
        // password from server is not filled in and a placeholder is displayed instead
        // so if password was not edited, assume it is filled in
        this._edited.push('password');
      }
      if (ldapConfig.udn !== '') {
        this._edited.push('udn');
      }
    }
  }

  handleRequiredChange (name: string, value: any): void {
    if (!this._edited.includes(name)) {
      this._edited.push(name);
    }

    if (!this.state.isDirty) {
      this.setState({isDirty: true});
    }

    this.props.onChangeValue(name, value);
  }

  handleChange (name: string, value: any): void {
    if (!this.state.isDirty) {
      this.setState({isDirty: true});
    }

    this.props.onChangeValue(name, value);
  }

  handleSubmit () {
    if (this.props.ldapConfig != null) {
      const ldapConfig: LdapConfig = this.props.ldapConfig;

      if (this.props.isFormValid) {
        this.props.onSave(ldapConfig);
        this.props.onClose();
      }
    }
  }

  override render (): React.ReactNode {
    const { show, ldapConfig, onClose,
      language, isFormValid
    } = this.props;

    if (ldapConfig == null) {
      return null;
    }

    const allElementsEdited: boolean = (
      this._edited.filter(name => this._required.includes(name)).length === this._required.length
    );

    const okDisabled: boolean = (
      !this.state.isDirty ||
      !allElementsEdited ||
      !isFormValid
    );

    const submit = (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      this.handleSubmit();
    };

    const isNew: boolean = ldapConfig.isNew();

    return (
      <Dialog
        id='sa_dialog_ldapconfig'
        language={language}
        title={translate(isNew ? 'launcher.identityproviders.ldap.addtitle' : 'launcher.identityproviders.ldap.edittitle')}
        show={show}
        onClose={onClose}
        onConfirm={submit}
        disableConfirm={okDisabled}
        primaryButton='confirm'
      >
        <ValidationForm>
          <ParameterSet>
            <CheckBoxField id='ae_check_identityproviders_ldap_enable'
              name='enabled'
              label={translate('launcher.identityproviders.ldap.enable')}
              value={ldapConfig.enabled}
              onChangeValue={this.handleRequiredChange}
            />
            <TextField id='ae_text_identityproviders_ldap_server'
              name='server'
              value={ldapConfig.server}
              label={translate('launcher.identityproviders.ldap.server')}
              onChangeValue={this.handleRequiredChange}
              validations={['required']}
            />
            <TextField id='ae_text_identityproviders_ldap_systemuser'
              name='systemUser'
              value={ldapConfig.systemUser}
              label={translate('launcher.identityproviders.ldap.systemuser')}
              onChangeValue={this.handleRequiredChange}
              validations={['required']}
            />
            <TextField id='ae_text_authentication_ldap_password'
              name='password'
              value={ldapConfig.isPasswordEdited || ldapConfig.isNew() ? ldapConfig.password : PASSWORD_PLACEHOLDER}
              label={translate('launcher.identityproviders.ldap.password')}
              type='password'
              onChangeValue={this.handleRequiredChange}
              validations={['required']}
              autoComplete='off'
            />
            <TextField id='ae_text_authentication_ldap_udn'
              name='udn'
              label={translate('launcher.identityproviders.ldap.udn')}
              value={ldapConfig.udn}
              onChangeValue={this.handleRequiredChange}
              validations={['required']}
            />
            <TextField id='ae_text_authentication_ldap_groupmembership'
              name='groupMembership'
              label={translate('launcher.identityproviders.ldap.groupmembership')}
              value={ldapConfig.groupMembership}
              placeholder={translate('launcher.identityproviders.ldap.groupmembership.placeholder')}
              onChangeValue={this.handleChange}
            />
            <CheckBoxField id='ae_check_authentication_ldap_ldaps'
              name='ldaps'
              label={translate('launcher.identityproviders.ldap.ldaps')}
              value={ldapConfig.ldaps}
              onChangeValue={this.handleChange}
            />
          </ParameterSet>
        </ValidationForm>
      </Dialog>
    );
  }
}

const EditLdapConfigDialog = ValidationWrapper(EditLdapConfigDialogImpl);

export default hideable(EditLdapConfigDialog);
